// The creator-collections listing page — eb-ui route `/cc/:collectionId/`
// (basePath `/cc`), a single custom-SSR page. The eb-ui Django backend
// pre-fetched ALL primary page data and injected it via `window.__SERVER_DATA__`
// (`CoreApplicationContext & CollectionProps & gaSettings`). Here that object is
// RECONSTRUCTED in `getServerSideProps`:
//
//   (a) TRACED public Core reads (gSSP → BFF bridge, DATA-FLOW-GUIDE §2 — request
//       context + existing endpoints, no new Core surface):
//         - collection metadata   GET /api/v3/collections/{id}/?expand=organizer,image
//         - first page of events  GET /api/v3/collections/{id}/events/public/… (upcoming+past)
//         - organizer's other collections  GET /api/v3/organizers/{id}/collections/…
//         - organizer follow status   GET /api/v3/organizers/{id}/?expand=follow_status
//   (b) request CONTEXT (locale/user) — forwarded straight through (no fetch).
//   (c) documented NON-CRITICAL degrade for Django-only computed fields with no
//       traced public oracle: `other_events_you_may_like` (personalization shelf —
//       EMPTY on the live guest oracle) and the SEO block (meta_title/canonical/
//       hreflang/json_ld) → a DERIVED `<title>` (sibling TTD precedent), never
//       invented.
//
// Every enrichment runs under `Promise.allSettled` (never `Promise.all` — the
// AbortError trap in memory.md). The collection read is the ONE critical field:
// a genuine 404/403/410 maps to the eb-ui `collection_moved` error render
// (`CollectionError`) — the page's second, verified render path.
//
// CREATOR_COLLECTIONS_FIXTURES=1 (allowlisted non-prod stages only) renders the
// page from the verified-live-oracle fixture with NO chrome service calls —
// the DoD smoke-render + visual-parity gates run here (no creds/VPN needed).
import type { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { getShellServerSideProps } from '@eventbrite/valkyrie-app-shell/server';
import { getLocaleFromRequest } from '@eventbrite/webapp-locales';
import type { FooterProps, GlobalHeaderProps } from '@eventbrite/valkyrie-app-shell';
import Head from 'next/head';
import React from 'react';

import { FeatureFlagsProvider } from '@/lib/featureFlags';
import { getCollectionFixture, getCollectionMovedFixture } from '@/lib/fixtures/collection';
import { isFixtures } from '@/lib/bff/coreProxy';
import { reconstructServerData } from '@/lib/serverData';
import type { FeatureFlags } from '@/lib/featureFlags';
import { App } from '@/ported/app';
import type { CollectionServerData, ServerData } from '@/types';
import { EMPTY_GLOBAL_FOOTER, createDefaultLocaleInfo } from '@eventbrite/valkyrie-app-shell';
import type { GlobalFooterProps } from '@eventbrite/valkyrie-app-shell';

export interface CollectionPageProps {
    serverData: ServerData;
    featureFlags: FeatureFlags;
    header: GlobalHeaderProps;
    footer: FooterProps;
    locale: string;
}

/**
 * Extract the numeric collection id from the route segment. The live page uses
 * the SLUG form `/cc/practitioner-led-sessions-1457019/` (the numeric form
 * `/cc/1457019/` 301s to it); the id is the TRAILING numeric run of the segment.
 * A segment with no trailing number has no readable id → notFound (honest
 * degrade — we do not invent a collection for an unresolvable id).
 *
 * BUGBOT (High): previously this matched the FIRST digit run (`/(\d+)/`), so a
 * slug containing earlier digits (e.g. a year like `2024-talks-1457019`)
 * resolved the wrong Core id → gSSP loaded the wrong collection or the
 * moved/error path. Match the trailing run only.
 */
// The slug→id parser lives in `src/ported/lib` (a pure, dependency-free
// module) so the Bugbot regression test can import it without dragging in the
// page module's ESM-only app-shell import graph.
import { collectionIdFromSegment } from '@/ported/lib/collectionIdFromSegment';

function jsonSafe<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
}

function fallbackHeader(path: string): GlobalHeaderProps {
    return {
        env: { serverUrl: 'https://www.eventbrite.com' },
        request: { path },
    } as GlobalHeaderProps;
}
function fallbackFooter(req: GetServerSidePropsContext['req']): FooterProps {
    return {
        serverUrl: 'https://www.eventbrite.com',
        localeInfo: createDefaultLocaleInfo(req),
        isUserAuthenticated: false,
        globalFooterProps: EMPTY_GLOBAL_FOOTER as GlobalFooterProps,
    };
}

export const getServerSideProps: GetServerSideProps<CollectionPageProps> = async (
    context: GetServerSidePropsContext,
) => {
    const collectionId = collectionIdFromSegment(context.params?.collectionId);
    if (!collectionId) {
        return { notFound: true };
    }

    const requestLocale = context.req ? getLocaleFromRequest(context.req) : 'en_US';

    // Fixtures mode — render the verified-live-oracle page with NO chrome
    // service calls (drives smoke-render + visual-parity with no creds/VPN).
    if (isFixtures()) {
        const shellResult = await getShellServerSideProps(context).catch(() => null);
        const movedRequested = collectionId === '9999999'; // dedicated moved fixture
        const previewRequested = String(context.query?.preview ?? '') === 'true';
        const fixture = movedRequested
            ? getCollectionMovedFixture()
            : getCollectionFixture();
        if ('collection_moved' in fixture) {
            fixture.preview = previewRequested;
        } else {
            fixture.preview = previewRequested;
        }
        const serverData = fixture;
        return {
            props: jsonSafe<CollectionPageProps>({
                serverData,
                featureFlags: (serverData.featureFlags || {}) as FeatureFlags,
                header: shellResult?.header ?? fallbackHeader(serverData.request.path || `/cc/${collectionId}/`),
                footer: shellResult?.footer ?? fallbackFooter(context.req),
                locale: requestLocale.replace('_', '-'),
            }),
        };
    }

    // Real mode — reconstruct the page from the traced public reads. The
    // collection read is the critical field; a 404/403/410 → the moved error.
    const { data } = await reconstructServerData(context, collectionId);
    const shellResult = await getShellServerSideProps(context).catch(() => null);

    return {
        props: jsonSafe<CollectionPageProps>({
            serverData: data,
            featureFlags: (data.featureFlags || {}) as FeatureFlags,
            header: shellResult?.header ?? fallbackHeader(`/cc/${collectionId}/`),
            footer: shellResult?.footer ?? fallbackFooter(context.req),
            locale: requestLocale.replace('_', '-'),
        }),
    };
};

export default function CollectionPage({
    serverData,
    featureFlags,
    header,
    footer,
    locale,
}: CollectionPageProps) {
    const isMoved = 'collection_moved' in serverData && serverData.collection_moved;
    const collectionName = isMoved
        ? undefined
        : (serverData as CollectionServerData).collection?.name;
    // SEO: the Django-injected meta block (meta_title/canonical/hreflang/json_ld)
    // has no traced public oracle → a DERIVED title (sibling TTD precedent).
    const pageTitle = collectionName ? `${collectionName} | Eventbrite` : 'Collection | Eventbrite';

    return (
        <FeatureFlagsProvider value={featureFlags}>
            <Head>
                <title>{pageTitle}</title>
                <meta name="viewport" content="width=device-width, initial-scale=1" />
            </Head>
            <App
                serverData={serverData}
                header={header}
                footer={footer}
                locale={(serverData.env.localeInfo as { locale?: string }).locale || 'en_US'}
            />
        </FeatureFlagsProvider>
    );
}
